import InputError from '@/components/input-error';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import AuthLayout from '@/layouts/auth-layout';
import { redirect } from '@/routes/auth/google';
import { store } from '@/routes/login';
import { request } from '@/routes/password';
import { Form, Head, Link } from '@inertiajs/react';
import { useMemo, useState } from 'react';

function getLoginVia(): string {
    if (typeof window === 'undefined') return 'web';
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    if (source === 'android_app' || source === 'play_store') return 'android_app';
    if (source === 'ios_app' || source === 'app_store') return 'ios_app';
    const ua = navigator.userAgent.toLowerCase();
    // Android WebView often includes "wv"; no need for standalone so in-app WebView is detected
    if (ua.includes('wv')) return 'android_app';
    const standalone = window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: fullscreen)').matches;
    if (standalone && (ua.includes('android') || ua.includes('wv'))) return 'android_app';
    if (standalone && (ua.includes('iphone') || ua.includes('ipad'))) return 'ios_app';
    return 'web';
}

interface LoginProps {
    status?: string;
    canResetPassword: boolean;
    canRegister: boolean;
}

export default function Login({
    status,
    canResetPassword,
    canRegister,
}: LoginProps) {
    const [showPassword, setShowPassword] = useState(false);
    const loginVia = useMemo(getLoginVia, []);

    return (
        <AuthLayout
            title="Welcome Back!"
            description="Please log in to access your e-wallet and complete your transaction securely."
            status={status}
            canResetPassword={canResetPassword}
            canRegister={canRegister}
        >
            <Head title="Log in" />
            <Form
                {...store.form()}
                resetOnSuccess={['password']}
                className="mt-8 space-y-6"
                data={{ login_via: loginVia }}
            >
                {({ processing, errors }) => (
                    <div className="space-y-6">
                        {/* Email Field */}
                        <div className="space-y-2">
                            <Label
                                htmlFor="email"
                                className="block text-sm font-medium text-gray-900 dark:text-white"
                            >
                                Email or Username
                            </Label>
                            <div className="relative">
                                <Input
                                    id="email"
                                    type="email"
                                    name="email"
                                    required
                                    autoFocus
                                    tabIndex={1}
                                    autoComplete="email"
                                    placeholder="Enter your email"
                                    className="block h-14 w-full rounded-lg border-2 border-green-500 bg-white px-4 py-3 pr-12 text-gray-900 transition-all outline-none placeholder:text-gray-400 dark:border-green-500/50 dark:bg-[#152a18] dark:text-white dark:placeholder:text-gray-500"
                                />
                                <div className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-green-500 dark:text-green-400">
                                    <span className="material-symbols-outlined">
                                        mail
                                    </span>
                                </div>
                            </div>
                            <InputError message={errors.email} />
                        </div>

                        {/* Password Field */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="password"
                                    className="block text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    Password
                                </Label>
                                {canResetPassword && (
                                    <Link
                                        href={request()}
                                        className="text-sm font-semibold text-gray-600 transition-colors hover:text-gray-900 dark:text-white dark:hover:text-white"
                                        tabIndex={5}
                                    >
                                        Forgot Password?
                                    </Link>
                                )}
                            </div>

                            <div className="group relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    required
                                    tabIndex={2}
                                    autoComplete="current-password"
                                    placeholder="Enter your password"
                                    className="block h-14 w-full rounded-lg border-2 border-green-500 bg-white px-4 py-3 pr-12 text-gray-900 transition-all outline-none placeholder:text-gray-400 dark:border-green-500/50 dark:bg-[#152a18] dark:text-white dark:placeholder:text-gray-500"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                    className="absolute top-0 right-0 flex h-full items-center justify-center px-4 text-gray-500 transition-colors hover:text-gray-900 focus:outline-none dark:text-gray-400 dark:hover:text-white"
                                    tabIndex={-1}
                                >
                                    <span className="material-symbols-outlined text-[20px] text-green-500 dark:text-green-400">
                                        {showPassword
                                            ? 'visibility_off'
                                            : 'visibility'}
                                    </span>
                                </button>
                            </div>
                            <InputError message={errors.password} />
                        </div>

                        {/* Remember me */}
                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="remember"
                                name="remember"
                                tabIndex={3}
                            />
                            <Label
                                htmlFor="remember"
                                className="text-sm font-medium text-gray-600 dark:text-gray-300"
                            >
                                Remember me
                            </Label>
                        </div>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            className="flex h-12 w-full transform items-center justify-center rounded-lg bg-green-600 px-6 text-base font-bold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-700 hover:shadow-lg active:translate-y-0 dark:bg-green-600 dark:hover:bg-green-500"
                            tabIndex={4}
                            disabled={processing}
                            data-test="login-button"
                        >
                            {processing && <Spinner />}
                            Log In
                        </Button>

                        {/* Divider */}
                        <div className="relative flex items-center py-2">
                            <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
                            <span className="mx-4 flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">
                                or continue with
                            </span>
                            <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
                        </div>

                        {/* Social Login */}
                        <div className="grid grid-cols-1 gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex h-11 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-[#152a18] dark:hover:bg-white/5"
                                tabIndex={-1}
                                onClick={() =>
                                    (window.location.href = redirect.url({ query: { login_via: loginVia } }))
                                }
                            >
                                <svg
                                    className="h-5 w-5"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                        fill="#4285F4"
                                    ></path>
                                    <path
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        fill="#34A853"
                                    ></path>
                                    <path
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                        fill="#FBBC05"
                                    ></path>
                                    <path
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        fill="#EA4335"
                                    ></path>
                                </svg>
                                <span className="text-sm font-medium text-gray-700 dark:text-white">
                                    Continue with Google
                                </span>
                            </Button>
                        </div>

                        {/* Terms & Privacy links */}
                        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                            By logging in you agree to our{' '}
                            <Link
                                href="/terms"
                                className="font-medium text-green-600 underline hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                            >
                                Terms of Use
                            </Link>
                            {' · '}
                            <Link
                                href="/privacy"
                                className="font-medium text-green-600 underline hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                            >
                                Privacy Policy
                            </Link>
                        </p>
                    </div>
                )}
            </Form>
        </AuthLayout>
    );
}
